import { CloseOutlined } from '@ant-design/icons';
import { useResolutionInfo } from '@mm/hooks';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { abbreviateNumber } from '@mm/utils';
import { InputNumber, Pagination, PaginationProps } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridLabels,
  GridRow,
} from '../../../../../../../common/components/Grid/Grid';
import { TABLET_SMALL } from '../../../../../../constants/screenSize';
import arrow from '../../LeadsSearch/PurchaseContactsTable/down-left-pointing-arrow.png';
import styles from './VoyagerLeadsContactsTable.module.css';
import b2bLeadsSrc from './b2b-leads-empty-state.png';
import {
  CreateVoyagerContactsSearchParams,
  VoyagerLeadsSearchQuery,
} from '@mm/state/interfaces/voyagerLeads';
import {
  LEAD_COST_IN_CREDITS,
  MAX_CONTACTS_ALLOWED,
} from '../../LeadsSearch/PurchaseContactsTable/PurchaseContactsTable';
import {
  MOBILE_PURCHASE_LEADS_CELL_DATA,
  VOYAGER_LEADS_CELL_DATA,
} from '../../LeadsSearch/PurchaseContactsTable/render-purchase-table-cells';
import {
  selectVoyagerLeads,
  selectVoyagerLeadsCount,
} from '@mm/state/selectors/voyagerLeadsSelectors';
import { useCreateVoyagerLeads } from '../hooks/use-create-voyager-leads';
import { Tooltip } from '@mm/components';

type Props = {
  voyagerSearchQuery: VoyagerLeadsSearchQuery;
  setVoyagerSearchQuery: (params: VoyagerLeadsSearchQuery) => void;
};

export const VoyagerLeadsContactsTable = ({
  voyagerSearchQuery,
  setVoyagerSearchQuery,
}: Props) => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const voyagerLeads = (useSelector(selectVoyagerLeads) || []).filter(
    lead => !lead.converted,
  );
  const leadsCount = useSelector(selectVoyagerLeadsCount) || 0;

  const hasLeads = voyagerLeads.length > 0;
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const defaultSelectedLeadsCount =
    leadsCount > MAX_CONTACTS_ALLOWED ? MAX_CONTACTS_ALLOWED : leadsCount;
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);
  const [selectedLeadsCount, setSelectedLeadsCount] = useState<number>(0);
  const selectedAll = selectedLeadsCount === defaultSelectedLeadsCount;
  const hasSelectedLeads = selectedLeadsCount || !!selectedCheckboxes.length;

  const invalidSelectedLeadsCount = Number(selectedLeadsCount) > leadsCount;

  const totalLeadsCost =
    (selectedCheckboxes.length
      ? selectedCheckboxes.length
      : selectedLeadsCount < MAX_CONTACTS_ALLOWED
      ? selectedLeadsCount
      : MAX_CONTACTS_ALLOWED) * LEAD_COST_IN_CREDITS;

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;

  const GRID_TEMPLATE_COLUMNS = '1fr 1fr 1fr 1fr 1fr 1fr';

  const { createVoyagerLeads } = useCreateVoyagerLeads();

  const getPaginatedIndex = (idx: number) =>
    currentPage === 1
      ? idx
      : (currentPage - 1) * (voyagerSearchQuery?.perPage || 50) + idx;

  const isCheckboxChecked = (id: string, idx: number) => {
    const paginatedIndex = getPaginatedIndex(idx);

    return (
      paginatedIndex < selectedLeadsCount ||
      selectedCheckboxes.some(checkboxId => checkboxId === id)
    );
  };

  const isCheckboxDisabled = (idx: number) => {
    const paginatedIndex = getPaginatedIndex(idx);
    return selectedLeadsCount ? paginatedIndex >= selectedLeadsCount : false;
  };
  const onCheckboxClick = (id: string, idx: number) => {
    const isExisting = isCheckboxChecked(id, idx);

    if (isExisting) {
      setSelectedCheckboxes(current => [
        ...current.filter(checkboxId => checkboxId !== id),
      ]);
    } else {
      setSelectedCheckboxes(current => [...current, id]);
    }
  };

  const onSelectAllClick = () => {
    setSelectedCheckboxes([]);
    setSelectedLeadsCount(defaultSelectedLeadsCount);
  };

  const getUniqueIds = (values: string[]) => [...new Set(values)];

  const onPurchaseClick = async (e?: Event, id?: string) => {
    e?.stopPropagation();

    if (id) {
      setSelectedCheckboxes(current => [...current, id]);
    }

    const params = (
      selectedLeadsCount
        ? {
            query: voyagerSearchQuery.query,
            total: selectedLeadsCount,
          }
        : {
            ids: id
              ? getUniqueIds([...selectedCheckboxes, id])
              : getUniqueIds([...selectedCheckboxes]),
          }
    ) as CreateVoyagerContactsSearchParams;

    createVoyagerLeads(params);
  };

  const onResetSelectAll = () => {
    setSelectedLeadsCount(0);
    setSelectedCheckboxes([]);
  };

  const onChange: PaginationProps['onChange'] = page => {
    setCurrentPage(page);
    setVoyagerSearchQuery({
      ...voyagerSearchQuery,
      page,
    });
  };

  useEffect(() => {
    setSelectedCheckboxes([]);

    onResetSelectAll();
  }, [voyagerSearchQuery.query]);

  return (
    <div className={styles.container}>
      <div className={styles.bar}>
        {!!leadsCount && hasLeads && (
          <div className={styles.leadsCount}>
            {leadsCount.toLocaleString('en-US')} filtered
          </div>
        )}
        {!!hasLeads && (
          <div className={styles.numberInputWrapper}>
            {!selectedCheckboxes.length ? (
              <InputNumber
                min={1}
                max={MAX_CONTACTS_ALLOWED}
                value={selectedLeadsCount}
                onChange={value => {
                  setSelectedCheckboxes([]);
                  setSelectedLeadsCount(value || 1);
                }}
                status={invalidSelectedLeadsCount ? 'error' : undefined}
                disabled={leadsCount === 0}
              />
            ) : (
              selectedCheckboxes.length
            )}{' '}
            selected
            <CloseOutlined
              className={styles.closeIcon}
              onClick={onResetSelectAll}
            />
          </div>
        )}

        {!selectedAll && (
          <PrimaryButton
            disabled={!hasLeads}
            className={styles.secondaryButton}
            onClick={onSelectAllClick}
            color="secondary-blue"
          >
            Select All
          </PrimaryButton>
        )}

        <Tooltip
          title={`${abbreviateNumber(totalLeadsCost)} Credits`}
          placement="topRight"
          overlayClassName={classNames(styles.tip, {
            [styles.hide]: !hasSelectedLeads || isNewBillingSubscription,
          })}
        >
          <div>
            <PrimaryButton
              className={styles.primaryButton}
              disabled={
                !hasLeads || !hasSelectedLeads || invalidSelectedLeadsCount
              }
              onClick={() => onPurchaseClick()}
            >
              Purchase Leads
            </PrimaryButton>
          </div>
        </Tooltip>
      </div>
      <Grid
        className={classNames(styles.grid, {
          [styles.gridEmptyState]: !hasLeads,
        })}
      >
        {isMobile ? null : (
          <GridLabels
            className={classNames(styles.gridLabels, {
              [styles.gridLabelsEmptyState]: !hasLeads,
            })}
            gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
          >
            {VOYAGER_LEADS_CELL_DATA.map(item => {
              const isFirstNameColumns = item.key.includes('firstname');
              return (
                <div
                  className={classNames(styles.tableHeader, {
                    [styles.firstNameHeading]: isFirstNameColumns,
                  })}
                  key={item.key.toString()}
                >
                  {item.label}
                </div>
              );
            })}
          </GridLabels>
        )}
        <div className={isMobile ? styles.gridRowsMobile : styles.gridRows}>
          {voyagerLeads?.map((lead, idx) => {
            const isChecked = isCheckboxChecked(lead.id, idx);
            const isDisabled = isCheckboxDisabled(idx);

            const onClick = () => {
              if (isDisabled) {
                return;
              }
              onCheckboxClick(lead.id, idx);
            };
            return isMobile ? (
              <GridRow
                key={lead.id}
                className={classNames(styles.gridRowMobile, {
                  [styles.gridRowSelectedMobile]: isChecked,
                  [styles.disabledGridRow]: isDisabled,
                })}
                gridTemplateColumns="1fr 1fr"
                onClick={onClick}
              >
                {MOBILE_PURCHASE_LEADS_CELL_DATA.map(({ Cell, key }) =>
                  Cell({
                    label: [lead.firstName, lead.jobTitle, lead.companyName]
                      .filter(Boolean)
                      .join(', '),
                    key,
                    isChecked,
                    disabled: (selectedLeadsCount && isChecked) || isDisabled,
                    onClick: e => onPurchaseClick(e, lead.id),
                    isConverted: lead?.converted,
                  }),
                )}
              </GridRow>
            ) : (
              <GridRow
                key={lead.id}
                className={classNames(styles.gridRow, {
                  [styles.gridRowSelected]: isChecked,
                  [styles.gridRowConvertedLead]: lead?.converted,
                  [styles.disabledGridRow]: isDisabled,
                })}
                gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
                onClick={onClick}
              >
                {VOYAGER_LEADS_CELL_DATA.map(({ Cell, key }) => {
                  const label = key.includes('location')
                    ? `${lead.professionalCity || ''}${
                        lead.professionalCity && lead.professionalState
                          ? ','
                          : ''
                      } ${lead.professionalState || ''}`
                    : lead[key];

                  return Cell({
                    label,
                    key,
                    isChecked,
                    disabled: (selectedLeadsCount && isChecked) || isDisabled,
                    onClick: e => onPurchaseClick(e, lead.id),
                    isConverted: lead?.converted,
                  });
                })}
              </GridRow>
            );
          })}
        </div>
      </Grid>
      {Boolean(hasLeads) && (
        <div className={styles.paginationWrapper}>
          <Pagination
            current={currentPage}
            onChange={onChange}
            pageSize={voyagerSearchQuery?.perPage || 50}
            showSizeChanger={false}
            total={leadsCount}
            hideOnSinglePage
          />
        </div>
      )}
      {!hasLeads && (
        <>
          <div className={styles.emptyStateContainer}>
            <div className={styles.emptyStateText}>
              Selects filters to find contacts!
            </div>
            <img src={arrow} className={styles.emptyStateArrow} />
          </div>
          <div className={styles.descriptionBlock}>
            <div className={styles.emptyStateDescription}>
              <div>B2B Contacts</div>
              <div className={styles.emptyStateSmallText}>
                Filter, search, and purchase contacts and add them to a custom
                outreach automation to qualify and close leads.
              </div>
            </div>
            <img
              src={b2bLeadsSrc}
              className={styles.emptyStateDescriptionImage}
            />
          </div>
        </>
      )}
    </div>
  );
};
